import { type ApiClient, createApi } from '@lp/portal-ui';

/**
 * The one API client the app uses. It forwards to the real same-origin client by default; the dev
 * fixture mode (see main.tsx) swaps in a fake client before the first render.
 */
export const API_BASE = '/api/portal';

let onUnauthenticated: (() => void) | null = null;
/** The signed-in layout registers this so any 401 sends the user back to the login page. */
export function setUnauthenticatedHandler(fn: (() => void) | null) {
  onUnauthenticated = fn;
}
function notifyUnauthenticated() {
  onUnauthenticated?.();
}

let client: ApiClient = createApi(API_BASE, notifyUnauthenticated);
let resolveMedia: (key: string) => string = (key) => `/api/portal/media/${key.split('/').map(encodeURIComponent).join('/')}`;

export interface ClientOverride {
  client: ApiClient;
  /** How to turn a stored media key into a displayable URL. */
  mediaUrl?: (key: string) => string;
}

export function setApiClient(make: (notify: () => void) => ClientOverride) {
  const o = make(notifyUnauthenticated);
  client = o.client;
  if (o.mediaUrl) resolveMedia = o.mediaUrl;
}

export const api: ApiClient = {
  get<T>(path: string) {
    return client.get<T>(path);
  },
  post<T>(path: string, body?: unknown) {
    return client.post<T>(path, body);
  },
  put<T>(path: string, body?: unknown) {
    return client.put<T>(path, body);
  },
  del<T>(path: string) {
    return client.del<T>(path);
  },
  upload<T>(path: string, file: File, extra?: Record<string, string>) {
    return client.upload<T>(path, file, extra);
  },
};

/**
 * Display URL for a stored media key. Settings only return keys (logoKey, faviconKey, ogImageKey),
 * so the portal builds the URL from its own media route (`/api/portal/media/<key>`).
 */
export function mediaUrl(key: string | null | undefined): string | null {
  return key ? resolveMedia(key) : null;
}
